import React, { useContext } from 'react';
import { ApiContext } from 'src/context/apiContext';
import { BadgeCell } from 'src/components/BadgeCell';
import { OperatorFlowLinkListCell } from 'src/components/OperatorFlowLinkListCell';
import { StatusCell } from 'src/components/StatusCell';
import { VariableListCell } from 'src/components/VariableListCell';
import { ClientSideTable } from 'src/components/ClientSideTable';
import { LinkGroupCell } from 'src/components/LinkGroupCell';

interface OperatorFlowTableProps {
  loading: boolean;
  data?: OperatorFlowJob[];
  error: Error | null;
}

export const OperatorFlowTable = ({
  loading,
  data,
  error,
}: OperatorFlowTableProps) => {
  const { dispatch } = useContext(ApiContext);
  const columns = [
    {
      header: 'Operator Flow Job Id',
      sortingField: 'id',
      id: 'id',
      cell: (item: OperatorFlowJob) => (
        <LinkGroupCell
          links={[
            {
              text: item.id,
              href: `./${item.id}/visualization`,
            },
          ]}
          onClick={() => {
            /* istanbul ignore next */
            dispatch({
              type: 'setCurrentOperatorFlowJobId',
              payload: item.id,
            });
          }}
        />
      ),
    },
    {
      header: 'Redeploy',
      cell: (item: OperatorFlowJob) => <BadgeCell item={item} />,
      sortingField: 'badges',
      id: 'badges',
    },
    {
      header: 'Status',
      sortingField: 'status',
      cell: (item: OperatorFlowJob) => <StatusCell status={item.status} />,
      id: 'status',
    },
    {
      header: 'Created Date',
      sortingField: 'createdDate',
      id: 'createdDate',
      cell: (item: OperatorFlowJob) => {
        const date = new Date(item.createdDate * 1000);
        return date.toUTCString();
      },
    },
    {
      header: 'Variables',
      sortingField: 'variables',
      cell: (item: OperatorFlowJob) => (
        <VariableListCell variables={item.variables} />
      ),
      width: 190,
      id: 'variables',
    },
    {
      header: 'Region',
      sortingField: 'region',
      cell: (item: OperatorFlowJob) => item.region,
      id: 'region',
      width: 100,
    },
    {
      header: 'Starting Operator',
      sortingField: 'startingOperatorName',
      id: 'starting-operator-name',
      width: 200,
      cell: (item: OperatorFlowJob) => item.startingOperatorName,
    },
    {
      header: 'Redeployment Jobs',
      sortingField: 'redeployedOperatorFlowJobIds',
      id: 'redeployment-operator-flows',
      cell: (item: OperatorFlowJob) => <OperatorFlowLinkListCell item={item} />,
    },
  ];

  return (
    <ClientSideTable
      error={error}
      loading={loading}
      title="Operator Flows"
      columnDefinitions={columns}
      data={data || []}
      emptyText="There are no operator flows for the given time filter."
    />
  );
};
